import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Copy, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";

export default function MySitePage() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/tenants/mine");
    setTenant(data);
    if (data) {
      setForm({
        name: data.name || "",
        specialty: data.specialty || "",
        bio: data.bio || "",
        location: data.location || "",
        phone: data.phone || "",
        photo_url: data.photo_url || "",
        services: (data.services || []).join(", "),
        accept_bookings: data.accept_bookings !== false,
      });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.id}`, {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Public site updated");
      load();
    } catch (err) {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  if (!tenant) {
    return (
      <div className="space-y-5">
        <h1 className="text-3xl font-bold tracking-tight">My Public Site</h1>
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          No public site is associated with your account. Ask the system admin to set up your tenant.
        </CardContent></Card>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/d/${tenant.slug}`;

  return (
    <div className="space-y-5 fade-up max-w-3xl" data-testid="my-site-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Public Site</h1>
        <p className="text-sm text-muted-foreground mt-1">This is what new patients see when they visit your booking page.</p>
      </div>

      <Card>
        <CardContent className="p-5 flex items-center gap-3 bg-muted/30">
          <ExternalLink className="size-4 text-primary" />
          <span className="mono text-sm flex-1 truncate">{publicUrl}</span>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="gap-1.5">
            <Copy className="size-3.5" /> Copy link
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm" data-testid="open-public-site">Open</Button>
          </a>
        </CardContent>
      </Card>

      <form onSubmit={save}>
        <Card>
          <CardHeader><CardTitle>Public profile</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5"><Label>Practice name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Specialty</Label>
              <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Bio</Label>
              <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Services (comma-separated)</Label>
              <Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Photo URL</Label>
              <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." /></div>
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="space-y-0.5">
                <Label>Accept bookings</Label>
                <div className="text-xs text-muted-foreground">When off, the public form is disabled.</div>
              </div>
              <Switch checked={form.accept_bookings} onCheckedChange={(v) => setForm({ ...form, accept_bookings: v })} />
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={saving} className="gap-2" data-testid="save-site-btn">
            <Save className="size-4" /> {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
